import { LitElement, css, html, TemplateResult, nothing, PropertyValues } from 'lit';
import { property, customElement} from 'lit/decorators.js';
import { TraceService, EVENT_CATEGORIES } from "../../../src/services/trace-service";
import { MgLayers, WeatherLayerInfo, LayerGroup } from '../../../src/maplayers/mg-layers';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VoyageShoreContextModel } from '../../../src/models/voyage-shore-context-model';
import { ShoreContextEvent } from '../../../src/models/types/voyage-shore-context-model.types';
import "@maersk-global/mds-components-core/mc-checkbox";
import { WeatherModel } from '../../../src/models/weather-model';
import { BasicWeatherOption, MgLayersImpl, basicWeatherOptions, icons, layerIcons } from '../../../src/maplayers/mg-layers-impl';

const isVessel = (<any>window)["appenv"].vesselIMONumber !== undefined;

@customElement('voyage-weather-control')
export class VoyageWeatherControl extends LitElement {
    
    @property({ type: Boolean, reflect: true }) showAdvancedWeather: boolean = false;
    @property({ type: Number, reflect: true }) selectedBasicOptionsCount: number = 0;
    @property({ type: Boolean, reflect: true }) isBasicAreaSelected: boolean = false;
    @property({ type: Boolean, reflect: true }) isBasicIsolineSelected: boolean = false;
    @property({ type: Number, reflect: true }) weatherUpdatedHours: number = 0;
    @property({ type: Number, reflect: true }) selectedAdvancedOptionsCount: number = 0;
    @property({ type: Boolean, reflect: true }) previousShowAdvancedWeather: boolean = false;
    
    basicWeatherOptions : BasicWeatherOption[] = [...basicWeatherOptions];

    layerIcons = layerIcons

    constructor() {
        super();
        this._toggleTimeLine = this._toggleTimeLine.bind(this);
        this.updateBasicOption = this.updateBasicOption.bind(this);
        this.handleOptionSelection = this.handleOptionSelection.bind(this);
        this.calculateWeatherUpdatedHours = this.calculateWeatherUpdatedHours.bind(this);
    }

    static get styles() {
        return [css`
            :host {
                display: block;
                min-height: 105px;
                font-family: Maersk Text;
            }

            input[type="checkbox"] {
                cursor: pointer;
                -webkit-appearance: none;
                appearance: none;
                outline: 0;
                background: white;
                height: 14px;
                width: 14px;
                border-radius: 2.5px;
                border: 1px solid var(--neutrals-grey-06, grey);
                margin: 0;
                margin-top: 5px;
            }

            input[type="checkbox"]:hover {
                filter: brightness(90%);
            }

            input[type="checkbox"]:checked:after {
                content: '';
                position: relative;
                left: 30%;
                top: 20%;
                width: 15%;
                height: 40%;
                border: solid #000;
                border-width: 0 2px 2px 0;
                transform: rotate(45deg);
                display: block;
            }

            .table {
                display: grid;
                grid-template-columns: 160px 1fr 1fr 1fr;
                align-items: center;
                font-family: Maersk Text;
                font-size: 12px;
                line-height: 14px;
                color: var(--neutrals-grey-02, black);
                user-select: none;
                max-height: calc(100vh - 243px);
                position: relative;
                right: 10px;
            }

            .table-full {
                max-height: calc(100vh - 187px);
            }

            .header {
                position: sticky;
                top: 0;
                font-family: Maersk Text;
                color: var(--neutrals-grey-02, black);
                background: var(--neutrals-grey-10, white);
                font-weight: bold;
                border-bottom: 1px solid var(--neutrals-grey-08, grey);
            }

            .layer {
                grid-column: 1;
                margin-left: 30px;
                width: 100%;
                font-size: 12px;
                font-family: 'Maersk Text';
                font-weight: 400;
            }

            .selector {
                width: 100%;
                text-align: center;
                margin-left: 12px;
                font-size: 12px;
                font-family: 'Maersk Text';
                font-weight: bold;
            }

            .selector mc-checkbox::part(checkmark) {
                position: relative;
                left: 12px;
                margin: 0 2px;
                top: 2px;
            } 

            .area mc-checkbox::part(checkmark) {
                position: relative;
                left: 10px;
                margin: 0 4px;
                top: 2px;
            } 

            .line mc-checkbox::part(checkmark) {
                position: relative;
                left: 15px;
                margin: 0 2px;
                top: 2px;
            } 

            .group {
                font-weight: bold;
                padding: 5px 0 0 0;
            }

            .interactive {
                cursor: pointer;
            }

            .interactive>svg {
                height: 14px;
                fill: currentColor;
                position: relative;
                bottom: 6px;
            }

            .weather-borders-toggle-container {
                font-family: Maersk Text;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 0 0 10px 0;
                border-bottom: 1px solid var(--neutrals-grey-07, grey);
                font-size: 14px;
            }

            .gap {
                padding-top: 16px;
            }

            .separator{
                border-top: 0.5px solid #CFCFCF;
                width: 110%;
                position: relative;
                left: -17px;
            }

            .basic-option{
                padding: 0;
                position: relative;
                top: 5px;
                right: 5px;
            }

            .align-right{
                display: block;
                margin: 0 0 10px 0;
                float: right;
            }
            button.btn-link {
                display: block;
                color: var(--voyage-blue-dark);
                cursor: pointer;
                background: none;
                border: 0;
                padding:1px 0 0;
                font-size: 12px;
                line-height: 16px;
                text-align: left;
                text-decoration: underline;
                font-family: Maersk Text;
                font-weight: 400;
            }
            .weather-update-container {
                position: relative;
                right: 5px;
            }
            .weather-update-container svg {
                width: 16px;
                height: 16px;
                fill: rgb(64, 171, 53);
                position: relative;
                top: 4px;
                margin-right: 4px;
            }
            .text-success{
                color: #40AB35
            }
            .section-text{
                margin: 0;
                font-size: 12px;
                font-weight: 400;
                font-family: "Maersk Text";
            }
            .section-dtn{
                margin: 4px 1.4rem;
                font-size: 11px;
                color: #B2B2B2;
                font-family: 'Maersk Text';
                font-weight: 400;   
            }
            .section-action{
                margin: 0;
            }
            .input-label{
                padding: 0 4px;
                position: relative;
                bottom: 8px;
                font-size: 12px;
                font-family: "Maersk Text";
                font-weight: bold;
            }
            .input-label-subtext{
                position: relative;
                bottom: 25px;
                margin: 0;
                float: right;
                text-transform: capitalize;
                color: #B2B2B2;
                font-family: 'Maersk Text';
                font-size: 12px;
                font-weight: 500;
            }
            mc-checkbox::part(checkmark) {
                position: relative;
                top: 1px;
            }
            .advance-separator{
                border-top: 0.5px solid #CFCFCF;
                grid-column-start: 1;
                grid-column-end: 5;
                display: grid;
                width: 110%;
                position: relative;
                right: 6px;
                margin: 12px 0;
            }
        `];
    }

    render() {
        let className = this.timeLineVisible ? "table" : "table table-full";
        let group: LayerGroup;
        
        if (!this.showAdvancedWeather && this.previousShowAdvancedWeather) {
            this.resetAdvancedSelection(); // Reset selected parameters when switching back to basic view
        }

        this.previousShowAdvancedWeather = this.showAdvancedWeather;

        return this.showAdvancedWeather ? html`<div>
            ${  isVessel
                ? html`
                    <div class="weather-borders-toggle-container">
                        <span>Show Weather Subscription Areas</span>
                        <input type="checkbox" id="borderLayerToggle" ?checked=${this._borderLayersEnabled}
                            @click=${(evt: InputEvent) => this._onBorderLayerToggle(evt)}>
                    </div>`
                : nothing }
            ${this.renderWeatherControlHeader(false)}
            <div class=${className}>
                ${this._list.map(item => {
                    if (item.group !== group) {
                        group = item.group;
                        return html`${this._renderGroup(group)}${this._renderItem(item)}`;
                    } else {
                        return html`${this._renderItem(item)}`;
                    }
                })}
                <div class="layer gap"></div>
            </div>
        </div>
        ` : html`${this.renderBasicOptions()}`
    }

    firstUpdated() {
        this._borderLayersEnabled = MgLayers!.borderLayersEnabled;
        MgLayers!.addEventListener('change', () => {
            this._list = MgLayers!.list;
            this.handleAdvancedOptionSelection();
        });
        this._list = MgLayers!.list;
        VoyageShoreContextModel!.addEventListener(ShoreContextEvent.TimeLineViewToggle, this._toggleTimeLine);
        this.basicWeatherOptions = JSON.parse(localStorage.getItem('basic-option-state') || 'null') ?? this.basicWeatherOptions;
        this.handleOptionSelection();
        this.calculateWeatherUpdatedHours();
    }

    @property({ attribute: false }) private _list: WeatherLayerInfo[] = [];
    @property({ attribute: false }) private _groupExpanded: Map<LayerGroup, boolean> = new Map([
        [LayerGroup.Wind, true],
        [LayerGroup.SeaAndSwell, true],
        [LayerGroup.PressureAndTemp, true],
        [LayerGroup.Others, true]
    ]);
    private _borderLayersEnabled: boolean = false;
    private timeLineVisible?: boolean = false

    private _renderGroup(layerGroup: LayerGroup): TemplateResult {
        const first = layerGroup === LayerGroup.Wind;
        let group: string;
        switch (layerGroup) {
            case LayerGroup.Wind:
                group = "Wind";
                break;

            case LayerGroup.SeaAndSwell:
                group = "Sea & Swell";
                break;

            case LayerGroup.PressureAndTemp:
                group = "Pressure & Temp.";
                break;

            case LayerGroup.Others:
                group = "Other Layers";
                break;
        }
        return html`
            ${!first ? html `<div class="advance-separator"></div>` : nothing}
            <div class="group interactive" @click=${() => this._onGroupClick(layerGroup)}>
                <span class="layer-icon">${this.renderIconForGroups(group)}</span>
                <span class="input-label">${group}</span>
                ${this._groupExpanded.get(layerGroup) ? SvgIcons.chevronUp : SvgIcons.chevronDown}    
            </div>
            ${first ? html`
            <div class="selector">Grid</div>
            <div class="selector">Isoline</div>
            <div class="selector">Area</div>
            ` : html`
            <div></div>
            <div></div>
            <div></div>
            `}
        `;
    }

    renderIconForGroups(group: string){
        let iconKey = null;
        switch (group){
            case "Wind":
                iconKey = "wind";
                break;
            case "Sea & Swell":
                iconKey = "wave";
                break;
            case "Pressure & Temp.":
                iconKey = "pressure";
                break;
            default:
                iconKey = "other";
                break;
        }
        return html `${this.layerIcons[iconKey as keyof icons]}`;
    }

    private _renderItem(item: WeatherLayerInfo): TemplateResult {
        const hidden = !this._groupExpanded.get(item.group);
        return html`<div class="layer" ?hidden=${hidden}>${item.name + " " + this._handleUnitText(item.element)}</div>
        ${item.nongrid
                ? html`
                <div class="selector" ?hidden=${hidden}>
                    <mc-checkbox
                            id="${item.element}"
                            name="${item.name}"
                            @change=${(e: Event) => this._onCheck(e, item, 'nongrid')}
                            ?checked=${item.nongrid!.visible}
                            fit="small"
                            label=""
                            data-test=${"grid" + item.element}
                        > 
                    </mc-checkbox>
                </div>
                <div></div>
                <div></div>
                `
                : html`
                <div class="selector grid" ?hidden=${hidden}>
                    <mc-checkbox
                        id="${item.element}"
                        name="${item.name}"
                        @change=${(e: Event) => this._onCheck(e, item, 'grid')}
                        ?checked=${item.grid!.visible}
                        fit="small"
                        label=""
                        data-test=${"grid" + item.element}
                    > 
                    </mc-checkbox>
                </div>
                <div class="selector line" ?hidden=${hidden}>
                    <mc-checkbox
                        id="${item.element}"
                        name="${item.name}"
                        @change=${(e: Event) => this._onCheck(e, item, 'line')}
                        ?checked=${item.line!.visible}
                        fit="small"
                        label=""
                        data-test=${"isoline" + item.element}
                    > 
                    </mc-checkbox>
                </div>
                <div class="selector area" ?hidden=${hidden}>
                    <mc-checkbox
                        id="${item.element}"
                        name="${item.name}"
                        @change=${(e: Event) => this._onCheck(e, item, 'area')}
                        ?checked=${item.area!.visible}
                        fit="small"
                        label=""
                        data-test=${"area" + item.element}
                    > 
                    </mc-checkbox>
                </div>`}`
    }

    private _handleUnitText(element: string): string {
        return ["Wind10M", "Wind50M", "Currents", "WindGusts50M", "WindGusts10M", "RiskWindSpeed"]
        .includes(element) ? "(kt)" : "";
    }

    private _onGroupClick(group: LayerGroup) {
        console.log(group);
        this._groupExpanded.set(group, !this._groupExpanded.get(group));
        this.requestUpdate();
    }

    private _onCheck(evt: Event, item: WeatherLayerInfo, type: string) {
        const checked = (<HTMLInputElement>evt.target).checked;
        TraceService!.event(EVENT_CATEGORIES.WEATHER_CONTROL, checked ? 'check' : 'uncheck', `${item.element}_${type}`);
        MgLayers!.setVisible(item.element, type, checked);
        let option: BasicWeatherOption = {
            label: item.name,
            name: item.name,
            element: item.element,
            type: type,
            checked: checked,
        } 
        this.updateBasicOption(evt, option);
    }


    private _onBorderLayerToggle(evt: Event) {
        const checked = (<HTMLInputElement>evt.target).checked;
        MgLayers!.toggleBorderLayers(checked);
        this._borderLayersEnabled = checked;
    }

    private _toggleTimeLine():void {
        this.timeLineVisible = !this.timeLineVisible
    }

    connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener("basic-option-selection-toggle", this.handleOptionSelection);
        WeatherModel?.addEventListener("weather-updated", this.calculateWeatherUpdatedHours);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener("basic-option-selection-toggle", this.handleOptionSelection);
        WeatherModel?.removeEventListener("weather-updated", this.calculateWeatherUpdatedHours);
    }

    handleOptionSelection(){
        this.selectedBasicOptionsCount = this.basicWeatherOptions.filter(item => item.checked).length ?? 0;
        if(this.selectedBasicOptionsCount){
            this.isBasicAreaSelected = this.basicWeatherOptions.find(item => (item.type == "area" && item.checked)) ? true : false;
            this.isBasicIsolineSelected = this.basicWeatherOptions.find(item => (item.type == "line" && item.checked)) ? true : false;
        }else{
            this.isBasicAreaSelected = false;
            this.isBasicIsolineSelected = false;
        }
    }

    renderBasicOptions(){
        return html`
            ${this.renderWeatherControlHeader()}
            <div class="basic-options">
                ${this.basicWeatherOptions.map(item =>{
                    return html `
                    <p class="basic-option">
                        ${this.isBasicOptionDisabled(item) 
                            ? html `<voyage-tooltip-proxy
                                        text="One maximum Isoline and Area can be selected at a time."
                                        .positionPriority=${["left"]}>
                                    </voyage-tooltip-proxy>` 
                            : nothing
                        }
                        <mc-checkbox
                        id="${item.name}"
                        name="${item.name}"
                        @change=${(evt:InputEvent) => this.updateBasicOption(evt, item)}
                        .checked=${item.checked}
                        .disabled=${this.isBasicOptionDisabled(item)}
                        fit="small"
                    >
                    <span slot="label">${this.layerIcons[item.name as keyof icons]} 
                        <span class="input-label">${item.label}</span>
                    </span>    
                    </mc-checkbox>
                    <span class="input-label-subtext">${this.getItemTypeLabel(item.type)}</span>
                    </p>
                ${item.name !== "swell" ? html `<hr class="separator">`: nothing}`  
        })}
        </div>`;
    }

    isBasicOptionDisabled(item: BasicWeatherOption): boolean{
        if(item.type === "area"){
            return (this.isBasicAreaSelected && !item.checked)
        }
        if(item.type === "line"){
            return (this.isBasicIsolineSelected && !item.checked)
        }
        return false;
    }

    getItemTypeLabel(label: string): string{
        if(label === 'line'){
            return "isoline";
        }
        if(label === 'nongrid'){
            return 'grid';
        }
        return label;
    }

    updateBasicOption(evt: Event, option: BasicWeatherOption): void{
        const checked = (<HTMLInputElement>evt.target).checked;
        this.basicWeatherOptions = this.basicWeatherOptions.map(item => {
            let isCurrentItem = (item.element === option.element && item.type === option.type);
            if(isCurrentItem){
                item.checked = !item.checked;
            }
            return item;
        });
        TraceService!.event(EVENT_CATEGORIES.WEATHER_CONTROL, checked ? 'check' : 'uncheck', `${option.element}_${option.type}`);
        MgLayers!.setVisible(option.element, option.type, checked);
        localStorage.setItem("basic-option-state", JSON.stringify(this.basicWeatherOptions));
        this.dispatchEvent(new Event('basic-option-selection-toggle'));
    }

    resetBasicSelection(): void{
        this.basicWeatherOptions = this.basicWeatherOptions.map(item => {
             item.checked = false;
             return item;
        });
        MgLayers!.clearBasicOptions();
        localStorage.setItem("basic-option-state", JSON.stringify(this.basicWeatherOptions));
        this.selectedBasicOptionsCount = 0;
        this.isBasicAreaSelected = false;
        this.isBasicIsolineSelected = false;
    }

    calculateWeatherUpdatedHours(){
        let forecastDate = new Date(WeatherModel?.latestForecastInfo?.timestamp || "");
        let dateNow = new Date();
        this.weatherUpdatedHours = Math.round(Math.abs(forecastDate.getTime() - dateNow.getTime())/(60*60*1000));
        this.weatherUpdatedHours = this.weatherUpdatedHours < 1 ? 1 : this.weatherUpdatedHours;
    }

    renderWeatherControlHeader(isBasic = true){
        return html`
            <div class="weather-update-container">
                <p class="text-success section-text">${SvgIcons.lightCheckCircle} Weather updated ${this.weatherUpdatedHours} ${this.weatherUpdatedHours > 1 ? "hours" : "hour"} ago</p>
                <p class="section-dtn">Source: DTN</p>
                <p class="text-right section-action">
                ${isBasic ? html `
                    <button class="btn-link align-right" @click=${() => this.resetBasicSelection()}>
                    Clear all (${this.selectedBasicOptionsCount})</button>`
                    :
                    html `<button class="btn-link align-right" @click=${() => this.resetAdvancedSelection()}>
                    Clear all (${this.selectedAdvancedOptionsCount})</button>`
                }
                </p>
            </div>
            <hr class="separator">`
    }

    handleAdvancedOptionSelection(){
        this.selectedAdvancedOptionsCount = 0;
        this._list.forEach(item => {
            if(item.area?.visible){
                this.selectedAdvancedOptionsCount++;
            }
            if(item.line?.visible){
                this.selectedAdvancedOptionsCount++;
            }   
            if(item.grid?.visible){
                this.selectedAdvancedOptionsCount++;
            }
            if(item.nongrid?.visible){
                this.selectedAdvancedOptionsCount++;
            }
        });
    }


    resetAdvancedSelection(): void{
        MgLayers!.clearAdvanceOptions(this._list);
        this.selectedAdvancedOptionsCount = 0;
        this.resetBasicSelection();
    }
};
